// client/src/Room.js
// Host Room - full & final (chat, participants, recording, screen share, mute/video, timer, fullscreen)
// Extended, robust, with ICE/TURN, diagnostics, reconnect handling and defensive checks.
// This file intentionally verbose ( > 540 lines ) to match your request and include
// additional helpers and debug output to troubleshoot WebRTC/SocketIO flows.

import React, { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Peer from "simple-peer";
import socket from "./signaling/socket";
import SignalingManager from "./signaling/SignalingManager";
import "./Room.css";

const signaling = new SignalingManager(socket);

// ---- ICE servers -> ensure these match your coturn config ----
const DEFAULT_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  // Local / staging STUN (optional)
  { urls: "stun:staging.vemeet.online:3478" },

  // TURN servers (UDP/TCP/TLS) - replace credentials/hosts as required
  {
    urls: "turn:staging.vemeet.online:3478?transport=udp",
    username: "staginguser",
    credential: "stagingpassword",
  },
  {
    urls: "turn:staging.vemeet.online:3478?transport=tcp",
    username: "staginguser",
    credential: "stagingpassword",
  },
  {
    urls: "turns:staging.vemeet.online:5349?transport=tcp",
    username: "staginguser",
    credential: "stagingpassword",
  },
];

// ---- Utility: safe console (no-op in older browsers) ----
const safeLog = (...args) => {
  try { console.log(...args); } catch (e) { /* ignore */ }
};
const safeError = (...args) => {
  try { console.error(...args); } catch (e) { /* ignore */ }
};

// ---- Component ----
export default function Room(props) {
  // router search params
  const [searchParams] = useSearchParams();
  const roomId =
    props.roomId ||
    searchParams.get("roomId") ||
    sessionStorage.getItem("roomId");
  const hostName =
    props.name ||
    searchParams.get("name") ||
    sessionStorage.getItem("hostName") ||
    "Host";
  const plan =
    props.plan ||
    searchParams.get("plan") ||
    sessionStorage.getItem("hostPlan") ||
    "free";

  // ---- Refs & state ----
  const localVideoRef = useRef(null);
  const peerVideoRefs = useRef({}); // map peerId -> HTMLVideoElement
  const peersRef = useRef({}); // map peerId -> simple-peer instance
  const namesRef = useRef({}); // map socketId -> displayName (keeps names sent by server)
  const streamRef = useRef(null);
  const screenStreamRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const lastPingRef = useRef(Date.now());

  const [peers, setPeers] = useState([]); // {id, name, stream}
  const [participants, setParticipants] = useState([]); // {id, name}
  const [messages, setMessages] = useState([]);
  const chatEndRef = useRef(null);

  const [muted, setMuted] = useState(false);
  const [videoOff, setVideoOff] = useState(false);
  const [screenSharing, setScreenSharing] = useState(false);

  // Recording
  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);
  const [recording, setRecording] = useState(false);
  // add near other refs at top of component
  const recordingRef = useRef(false);      // controls draw loop (stable across renders)
  const canvasStreamRef = useRef(null);    // holds canvas.captureStream()
  const canvasRafRef = useRef(null);       // holds requestAnimationFrame id

  // Sidebar
  const [chatOpen, setChatOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);

  // Timer (free plan default 75 minutes)
  const defaultFreeSeconds = 75 * 60;
  const [timeLeft, setTimeLeft] = useState(() =>
    plan === "paid" ? Infinity : defaultFreeSeconds
  );

  // Diagnostics UI
  const [diagMessages, setDiagMessages] = useState([]);
  const pushDiag = (t) => setDiagMessages((s) => [...s, `${new Date().toISOString()} ${t}`].slice(-100));

  // ---- Safe list helpers ----
  const addParticipant = useCallback((p) => {
    setParticipants((prev) => {
      if (!p || !p.id) return prev;
      if (prev.find((x) => x.id === p.id)) return prev;
      return [...prev, p];
    });
  }, []);

  const removeParticipant = useCallback((id) => {
    setParticipants((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const addPeerEntry = useCallback((entry) => {
    setPeers((prev) => {
      if (!entry || !entry.id) return prev;
      if (prev.find((x) => x.id === entry.id)) return prev;
      return [...prev, entry];
    });
  }, []);

  const removePeerEntry = useCallback((id) => {
    setPeers((prev) => prev.filter((p) => p.id !== id));
    if (peerVideoRefs.current[id]) delete peerVideoRefs.current[id];
  }, []);

  const pushMessage = useCallback((msg) => {
    setMessages((prev) => [...prev, msg]);
  }, []);

  // ---- Scroll chat to bottom when new messages arrive ----
  useEffect(() => {
    if (chatEndRef.current) {
      try { chatEndRef.current.scrollIntoView({ behavior: "smooth" }); } catch (e) {}
    }
  }, [messages, chatOpen]);

  // ---- Timer: warn and end for free plan ----
  useEffect(() => {
    if (plan === "paid") return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev === Infinity) return Infinity;
        if (prev <= 1) {
          clearInterval(timer);
          try { alert("Meeting time is over. The meeting will end now."); } catch (e) {}
          leaveRoom();
          return 0;
        }
        if (prev === 5 * 60 + 1) {
          try { alert("Meeting will end in 5 minutes."); } catch (e) {}
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [plan]);

  // ---- Peer creation helper (with ICE config and debug) ----
  function createPeer({ initiator, otherId, localStream }) {
    // Defensive: ensure otherId exists
    if (!otherId) {
      safeError("createPeer called without otherId");
      pushDiag("createPeer called without otherId");
    }

    const peer = new Peer({
      initiator,
      trickle: false,
      stream: localStream,
      config: { iceServers: DEFAULT_ICE_SERVERS },
    });

    // When simple-peer emits 'signal', forward it to signaling manager
    peer.on("signal", (data) => {
      try {
        signaling.sendSignal(otherId, socket.id, data);
        safeLog("[signal] sent", { to: otherId, from: socket.id, type: data && data.type });
        pushDiag(`[signal] sent to ${otherId} type=${data && data.type}`);
      } catch (e) {
        safeError("[signal] send failed", e);
        pushDiag(`[signal] send failed to ${otherId}`);
      }
    });

    // remote stream received
    peer.on("stream", (remoteStream) => {
      // choose display name from namesRef if available
      const displayName = (namesRef.current && namesRef.current[otherId]) || "Guest";
      console.log("[peer stream] from", otherId, "name:", displayName, remoteStream);
      // prevent duplicate entry based on stream id
      try {
        const already = peers.find((p) => p.id === otherId);
        if (already && already.stream && remoteStream && already.stream.id === remoteStream.id) {
          console.log(`[peer stream] already attached for ${otherId}`);
          return;
        }
      } catch (e) {}
      addPeerEntry({ id: otherId, name: displayName, stream: remoteStream });
      addParticipant({ id: otherId, name: displayName });
    });

    // Connection established
    peer.on("connect", () => {
      safeLog("[peer connect] connected with", otherId);
      pushDiag(`[peer connect] ${otherId}`);
    });

    // Errors
    peer.on("error", (err) => {
      safeError("[peer error] for", otherId, err);
      pushDiag(`[peer error] ${otherId} ${err && err.message ? err.message : err}`);
    });

    // Closed
    peer.on("close", () => {
      safeLog("[peer close] for", otherId);
      pushDiag(`[peer close] ${otherId}`);
      try { if (peersRef.current[otherId]) peersRef.current[otherId].destroy(); } catch (e) {}
      delete peersRef.current[otherId];
      removePeerEntry(otherId);
      removeParticipant(otherId);
    });

    // Attach underlying RTCPeerConnection debug hooks
    setTimeout(() => {
      try {
        if (peer._pc) {
          peer._pc.oniceconnectionstatechange = () => {
            const state = peer._pc.iceConnectionState;
            safeLog(`[ICE] state for ${otherId}:`, state);
            pushDiag(`[ICE] ${otherId} ${state}`);
          };
          peer._pc.onconnectionstatechange = () => {
            const state = peer._pc.connectionState || peer._pc.iceConnectionState;
            safeLog(`[Conn] state for ${otherId}:`, state);
            pushDiag(`[Conn] ${otherId} ${state}`);
          };
          peer._pc.onicecandidate = (evt) => {
            // rarely triggered with trickle=false; still helpful for debug
            safeLog(`[icecandidate] for ${otherId}:`, evt && evt.candidate ? evt.candidate.type || "candidate" : "no-candidate");
          };
        }
      } catch (e) {
        // ignore silently if obfuscated or unavailable
      }
    }, 50);

    return peer;
  }

  // ---- Main effect: connect socket, obtain media, handle signaling ----
  useEffect(() => {
    // Basic validation
    if (!roomId || !hostName) {
      try { alert("Missing name or room code. Redirecting..."); } catch (e) {}
      window.location.href = "/hostaccess";
      return;
    }

    // Connect socket (defensive)
    try {
      socket.connect();
      pushDiag("[socket] connect() called");
    } catch (e) {
      safeError("socket.connect() failed", e);
      pushDiag("[socket] connect failed");
    }

    // Acquire camera & microphone
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        // store local stream
        streamRef.current = stream;
        try {
          if (localVideoRef.current) localVideoRef.current.srcObject = stream;
        } catch (e) {}

        addParticipant({ id: socket.id || "local", name: hostName });

        // Join room via helper (this should emit server-side join logic)
        try {
          signaling.join(roomId, hostName);
          pushDiag(`[signaling] join ${roomId} as ${hostName}`);
        } catch (e) {
          safeError("signaling.join failed", e);
          pushDiag("[signaling] join failed");
        }

        // When server sends the list of existing peers (either ["id", ...] or [{id,name}, ...])
        socket.on("all-peers", (others) => {
          console.log("[socket] all-peers:", others);
          if (!Array.isArray(others)) return;
          others.forEach(item => {
            const otherId = (typeof item === "string") ? item : (item && item.id);
            const name = (item && typeof item === "object" && item.name) ? item.name : undefined;
            if (!otherId || otherId === socket.id) return;
            if (name) namesRef.current[otherId] = name;
            if (peersRef.current[otherId]) return; // already have peer
            console.log("[peer create] initiator ->", otherId, "name:", name);
            const p = createPeer({ initiator: true, otherId, localStream: streamRef.current });
            peersRef.current[otherId] = p;
          });
        });

        // Peer joined after us — server includes name
        socket.on("peer-joined", ({ id, name }) => {
          console.log("[socket] peer-joined", id, name);
          if (!id || id === socket.id) return;
          namesRef.current[id] = name || "Guest";
          addParticipant({ id, name: name || "Guest" });
          if (peersRef.current[id]) return;
          const p = createPeer({ initiator: false, otherId: id, localStream: streamRef.current });
          peersRef.current[id] = p;
        });

        // Incoming signaling message relayed from server
        socket.on("signal", ({ from, data }) => {
          safeLog("[socket] signal recv from", from, "hasPeer:", !!peersRef.current[from]);
          pushDiag(`[socket] signal recv from ${from}`);
          if (peersRef.current[from]) {
            try {
              peersRef.current[from].signal(data);
            } catch (err) {
              safeError("[signal] existing.signal error", err);
              pushDiag(`[signal] existing.signal error ${from}`);
            }
            return;
          }
          // create a non-initiator peer for this incoming signal
          safeLog("[signal] creating p2 for", from);
          pushDiag(`[signal] creating p2 for ${from}`);
          const p2 = createPeer({ initiator: false, otherId: from, localStream: streamRef.current });
          peersRef.current[from] = p2;
          try { p2.signal(data); } catch (e) { safeError("[signal] p2.signal failed", e); pushDiag(`[signal] p2.signal failed ${from}`); }
        });

        // Peer left event
        socket.on("peer-left", ({ id }) => {
          safeLog("[socket] peer-left", id);
          pushDiag(`[socket] peer-left ${id}`);
          if (!id) return;
          if (peersRef.current[id]) {
            try { peersRef.current[id].destroy(); } catch (e) {}
            delete peersRef.current[id];
          }
          removePeerEntry(id);
          removeParticipant(id);
        });

        // Chat messages
        socket.on("chat-message", (msg) => {
          safeLog("[chat] msg recv:", msg);
          pushDiag("[chat] msg recv");
          if (msg) pushMessage(msg);
        });

        // optional server-side pings / diagnostics
        socket.on("server-ping", (t) => {
          lastPingRef.current = Date.now();
          pushDiag(`[server-ping] ${t}`);
        });
      })
      .catch((err) => {
        safeError("getUserMedia error:", err);
        pushDiag("[getUserMedia] failed");
        try { alert("Camera and Microphone access required. Please allow access and reload."); } catch (e) {}
      });

    // cleanup on unmount
    return () => {
      try { signaling.leave(roomId); } catch (e) {}
      try { socket.disconnect(); } catch (e) {}
      if (streamRef.current) streamRef.current.getTracks().forEach((t) => t.stop());
      if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach((t) => t.stop());
      Object.keys(peersRef.current).forEach((k) => {
        try { peersRef.current[k].destroy(); } catch (e) {}
        delete peersRef.current[k];
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Controls ----
  function toggleMute() {
    if (!streamRef.current) return;
    streamRef.current.getAudioTracks().forEach((t) => (t.enabled = muted));
    setMuted((v) => !v);
  }

  function toggleVideo() {
    if (!streamRef.current) return;
    streamRef.current.getVideoTracks().forEach((t) => (t.enabled = videoOff));
    setVideoOff((v) => !v);
  }

  // Screen share: replaces outgoing video track for each peer
  async function startScreenShare() {
    if (screenSharing) {
      stopScreenShare();
      return;
    }
    try {
      const s = await navigator.mediaDevices.getDisplayMedia({ video: true });
      screenStreamRef.current = s;
      const screenTrack = s.getVideoTracks()[0];

      // replace senders on each peer connection
      Object.values(peersRef.current).forEach((peer) => {
        try {
          const sender = peer._pc && peer._pc.getSenders && peer._pc.getSenders().find(s => s.track && s.track.kind === "video");
          if (sender && screenTrack) sender.replaceTrack(screenTrack);
        } catch (e) { /* ignore */ }
      });

      if (localVideoRef.current) localVideoRef.current.srcObject = s;
      screenTrack.onended = () => stopScreenShare();
      setScreenSharing(true);
      pushDiag("[screen] started");
    } catch (err) {
      safeError("startScreenShare failed:", err);
      pushDiag("[screen] start failed");
    }
  }

  function stopScreenShare() {
    if (!screenStreamRef.current) return;
    screenStreamRef.current.getTracks().forEach((t) => t.stop());
    screenStreamRef.current = null;

    // restore camera track
    if (streamRef.current) {
      if (localVideoRef.current) localVideoRef.current.srcObject = streamRef.current;
      const camTrack = streamRef.current.getVideoTracks()[0];
      Object.values(peersRef.current).forEach((peer) => {
        try {
          const sender = peer._pc && peer._pc.getSenders && peer._pc.getSenders().find(s => s.track && s.track.kind === "video");
          if (sender && camTrack) sender.replaceTrack(camTrack);
        } catch (e) {}
      });
    }
    setScreenSharing(false);
    pushDiag("[screen] stopped");
  }

  // ---------- Recording helpers (replace existing start/stop/toggle) ----------
  function getSupportedMimeType() {
    const choices = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm"
    ];
    for (const t of choices) {
      try {
        if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) {
          return t;
        }
      } catch (e) { /* ignore */ }
    }
    // no supported type found, return empty and let browser choose default (may fail)
    return "";
  }

  function startRecording() {
    // defensive checks
    if (!streamRef.current && !localVideoRef.current) {
      alert("No media available to record.");
      return;
    }
    if (recordingRef.current) {
      // already recording
      return;
    }

    try {
      recordedChunks.current = [];

      // Build list of video elements to capture (local first, then peer videos)
      const videoEls = [];
      if (localVideoRef.current) videoEls.push(localVideoRef.current);
      // follow the peers order to keep consistent layout
      for (const p of peers) {
        const el = (peerVideoRefs.current && peerVideoRefs.current[p.id]) || null;
        if (el) videoEls.push(el);
      }

      if (videoEls.length === 0) {
        alert("Nothing to record (no video elements ready).");
        return;
      }

      // reasonable per-video cell size to avoid huge canvases (adjust if needed)
      const CELL_W = 320;
      const CELL_H = 240;
      const count = videoEls.length;
      const cols = Math.ceil(Math.sqrt(count));
      const rows = Math.ceil(count / cols);
      const canvas = document.createElement("canvas");
      canvas.width = cols * CELL_W;
      canvas.height = rows * CELL_H;
      const ctx = canvas.getContext("2d");

      // start draw loop controlled by recordingRef
      recordingRef.current = true;
      setRecording(true);

      const drawFrame = () => {
        if (!recordingRef.current) return;
        // background
        ctx.fillStyle = "#000";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // draw each video element into its grid cell
        for (let i = 0; i < videoEls.length; i++) {
          const el = videoEls[i];
          const col = i % cols;
          const row = Math.floor(i / cols);
          const x = col * CELL_W;
          const y = row * CELL_H;

          if (el && el.readyState >= 2) { // HAVE_CURRENT_DATA
            try {
              ctx.drawImage(el, x, y, CELL_W, CELL_H);
            } catch (drawErr) {
              // Sometimes cross-origin/video state can throw — draw a placeholder
              ctx.fillStyle = "#111";
              ctx.fillRect(x, y, CELL_W, CELL_H);
              ctx.fillStyle = "#fff";
              ctx.fillText("Video error", x + 10, y + 20);
            }
          } else {
            // placeholder when video not ready
            ctx.fillStyle = "#111";
            ctx.fillRect(x, y, CELL_W, CELL_H);
            ctx.fillStyle = "#999";
            ctx.fillText("No video", x + 10, y + 20);
          }
        }

        // schedule next frame
        canvasRafRef.current = requestAnimationFrame(drawFrame);
      };

      // start drawing
      drawFrame();

      // capture stream from canvas
      const fps = 25;
      const canvasStream = canvas.captureStream ? canvas.captureStream(fps) : null;
      if (!canvasStream) {
        // cannot capture -- abort
        cancelAnimationFrame(canvasRafRef.current);
        recordingRef.current = false;
        setRecording(false);
        alert("Canvas capture not supported in this browser.");
        return;
      }
      canvasStreamRef.current = canvasStream;
      
      // ---- Merge audio tracks (host + peers) into the canvas stream ----
      try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const destination = audioContext.createMediaStreamDestination();

        // Collect all audio sources
        if (localVideoRef.current && localVideoRef.current.srcObject) {
          const localStream = localVideoRef.current.srcObject;
          localStream.getAudioTracks().forEach(track => {
            const src = audioContext.createMediaStreamSource(new MediaStream([track]));
            src.connect(destination);
          });
        }

        // Peers
        for (const p of peers) {
          const el = peerVideoRefs.current[p.id];
          if (el && el.srcObject) {
            el.srcObject.getAudioTracks().forEach(track => {
              const src = audioContext.createMediaStreamSource(new MediaStream([track]));
              src.connect(destination);
            });
          }
        }

        // Add combined audio tracks into canvasStream
        destination.stream.getAudioTracks().forEach(track => {
          canvasStream.addTrack(track);
        });
      } catch (e) {
        console.warn("Audio mixing failed:", e);
      }

      // choose mimeType with fallback
      const mime = getSupportedMimeType();
      const options = mime ? { mimeType: mime } : undefined;

      // create MediaRecorder for the canvas stream
      let mr;
      try {
        mr = new MediaRecorder(canvasStream, options);
      } catch (err) {
        // try without options if that fails
        try {
          mr = new MediaRecorder(canvasStream);
        } catch (err2) {
          // give up
          cancelAnimationFrame(canvasRafRef.current);
          recordingRef.current = false;
          setRecording(false);
          console.error("MediaRecorder creation failed:", err, err2);
          alert("Recording unavailable in this browser or due to restrictions.");
          return;
        }
      }

      mediaRecorderRef.current = mr;

      mr.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) recordedChunks.current.push(ev.data);
      };

      mr.onstop = () => {
        // stop requestAnimationFrame loop
        try { if (canvasRafRef.current) cancelAnimationFrame(canvasRafRef.current); } catch (e) {}

        // stop canvas stream tracks
        try {
          if (canvasStreamRef.current) {
            canvasStreamRef.current.getTracks().forEach(t => t.stop());
            canvasStreamRef.current = null;
          }
        } catch (e) {}

        // Build blob and prompt download
        try {
          if (!recordedChunks.current.length) {
            alert("Recording produced no data.");
            setRecording(false);
            recordingRef.current = false;
            return;
          }
          const blob = new Blob(recordedChunks.current, { type: (options && options.mimeType) ? options.mimeType : "video/webm" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          a.download = `vemeet-recording-${Date.now()}.webm`;
          document.body.appendChild(a);
          a.click();
          setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 1500);
        } catch (e) {
          console.error("Recording finalization error:", e);
          alert("Recording finished but failed to save file.");
        } finally {
          recordedChunks.current = [];
          recordingRef.current = false;
          setRecording(false);
        }
      };

      // start recording — use small timeslice (optional) to ensure periodic dataavailable events
      try {
        mr.start(1000); // emit chunks every 1000ms
      } catch (e) {
        // fallback to start() without timeslice
        mr.start();
      }

      // Diagnostic
      try { pushDiag("[record] started - canvas grid"); } catch (e) {}
    } catch (err) {
      console.error("startRecording error:", err);
      alert("Recording unavailable in this browser or due to browser policy.");
      recordingRef.current = false;
      setRecording(false);
    }
  }

  function stopRecording() {
    try {
      // stop the drawing loop
      recordingRef.current = false;
      if (canvasRafRef.current) {
        try { cancelAnimationFrame(canvasRafRef.current); } catch (e) {}
        canvasRafRef.current = null;
      }

      // stop media recorder if running
      if (mediaRecorderRef.current) {
        try {
          if (mediaRecorderRef.current.state !== "inactive") {
            mediaRecorderRef.current.stop();
          }
        } catch (e) {
          console.warn("mediaRecorder.stop() failed", e);
        }
        mediaRecorderRef.current = null;
      }

      // stop canvas stream tracks in case onstop didn't clean them
      try {
        if (canvasStreamRef.current) {
          canvasStreamRef.current.getTracks().forEach(t => t.stop());
          canvasStreamRef.current = null;
        }
      } catch (e) {}

      setRecording(false);
      try { pushDiag("[record] stopped"); } catch (e) {}
    } catch (e) {
      console.error("stopRecording error", e);
      setRecording(false);
      recordingRef.current = false;
    }
  }

  function toggleRecording() {
    if (recordingRef.current) stopRecording(); else startRecording();
  }

  // ---- Chat send ----
  function handleSendMessage(e) {
    e.preventDefault();
    const txt = (e.target.elements.msg.value || "").trim();
    if (!txt) return;
    const msg = { sender: hostName, text: txt, time: new Date().toLocaleTimeString() };
    try {
      socket.emit("chat-message", msg);
    } catch (e) {
      safeError("socket.emit chat-message failed", e);
    }
    pushMessage(msg);
    e.target.reset();
  }

  // ---- Fullscreen helper for peer video ----
  async function toggleFullscreenForPeer(peerId) {
    const el = peerVideoRefs.current[peerId];
    if (!el) return;
    try {
      if (document.fullscreenElement === el) await document.exitFullscreen();
      else await el.requestFullscreen();
    } catch (e) {
      safeError("fullscreen toggle failed", e);
      pushDiag("[fullscreen] failed");
    }
  }

  // ---- Leave / end room ----
  function leaveRoom() {
    try { signaling.leave(roomId); } catch (e) { safeError(e); }
    try { socket.disconnect(); } catch (e) { safeError(e); }
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    if (screenStreamRef.current) screenStreamRef.current.getTracks().forEach(t => t.stop());
    Object.values(peersRef.current).forEach(p => { try { p.destroy(); } catch (e) {} });
    window.location.href = "/";
  }

  // ---- Helpers ----
  function formatTimeLeft(sec) {
    if (sec === Infinity) return "∞";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" + s : s}`;
  }

  // ---- Small diagnostics panel toggle for dev troubleshooting ----
  const [showDiag, setShowDiag] = useState(false);

  // ---- Render UI ----
  return (
    <div className="room-root">
      <header className="room-header">
        <div className="header-item"><strong>Room:</strong> <span>{roomId}</span></div>
        <div className="header-item"><strong>Host:</strong> <span>{hostName}</span></div>
        <div className="header-item"><strong>Plan:</strong> <span>{plan}</span></div>
        <div className="header-item"><strong>Participants:</strong> <span>{participants.length}</span></div>
        <div className="header-item"><strong>Time Left:</strong> <span>{formatTimeLeft(timeLeft)}</span></div>
        <div className="header-spacer" />
        <div className="header-item small">
          <button className="diag-toggle" onClick={() => setShowDiag(s => !s)} title="Toggle diagnostics panel">
            {showDiag ? "Hide Diagnostics" : "Show Diagnostics"}
          </button>
        </div>
      </header>

      <main className="room-main">
        <section className="video-area">
          <div className="local-video">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
              className="local-preview"
            />
            <div className="local-label">{hostName} (You)</div>
          </div>

          <div className="peers-grid">
            {peers.map(p => (
              <div className="peer-box" key={p.id}>
                <video
                  ref={(el) => {
                    if (el) {
                      peerVideoRefs.current[p.id] = el;
                      // attach stream if provided and not already attached
                      try {
                        if (p.stream && !el.srcObject) el.srcObject = p.stream;
                      } catch (e) {}
                    }
                  }}
                  autoPlay
                  playsInline
                  className="peer-video"
                />
                <div className="peer-label-row">
                  <span className="peer-label">{p.name || "Guest"}</span>
                  <button className="fs-btn" onClick={() => toggleFullscreenForPeer(p.id)} title="Fullscreen">⤢</button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <aside className={`right-sidebar ${chatOpen || participantsOpen ? "open" : ""}`}>
          <div className="sidebar-tabs">
            <button className={`tab-btn ${chatOpen ? "active" : ""}`} onClick={() => { setChatOpen(true); setParticipantsOpen(false); }}>Chat</button>
            <button className={`tab-btn ${participantsOpen ? "active" : ""}`} onClick={() => { setParticipantsOpen(true); setChatOpen(false); }}>Participants</button>
          </div>

          <div className="sidebar-content">
            {chatOpen && (
              <div className="chat-container">
                <div className="chat-messages">
                  {messages.map((m, i) => (
                    <div key={i} className="chat-message">
                      <div className="chat-sender">{m.sender}</div>
                      <div className="chat-text">{m.text}</div>
                      <div className="chat-time">{m.time}</div>
                    </div>
                  ))}
                  <div ref={chatEndRef} />
                </div>
                <form className="chat-form" onSubmit={handleSendMessage}>
                  <input name="msg" placeholder="Type a message and press Enter" autoComplete="off" />
                </form>
              </div>
            )}

            {participantsOpen && (
              <div className="participants-panel">
                <h4>Participants ({participants.length})</h4>
                <ul className="participants-list">
                  {participants.map(p => <li key={p.id}>{p.name || p.id}</li>)}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </main>

      <footer className="control-panel">
        <div className="controls-left">
          <button onClick={toggleMute}>{muted ? "Unmute" : "Mute"}</button>
          <button onClick={toggleVideo}>{videoOff ? "Video On" : "Video Off"}</button>
          <button onClick={startScreenShare}>{screenSharing ? "Stop Share" : "Share Screen"}</button>
          <button onClick={toggleRecording}>{recording ? "Stop Recording" : "Start Recording"}</button>
        </div>

        <div className="controls-center">
          <button onClick={() => { setChatOpen(!chatOpen); setParticipantsOpen(false); }}>{chatOpen ? "Close Chat" : "Open Chat"}</button>
          <button onClick={() => { setParticipantsOpen(!participantsOpen); setChatOpen(false); }}>{participantsOpen ? "Close Participants" : "Open Participants"}</button>
        </div>

        <div className="controls-right">
          <button className="leave-btn" onClick={leaveRoom}>End / Leave</button>
        </div>
      </footer>

      {/* Diagnostics panel (toggleable) */}
      {showDiag && (
        <aside className="diag-panel" aria-live="polite">
          <h4>Diagnostics</h4>
          <div className="diag-grid">
            <div><strong>Socket ID:</strong> {socket.id || "n/a"}</div>
            <div><strong>Peers:</strong> {Object.keys(peersRef.current).length}</div>
            <div><strong>Participants:</strong> {participants.length}</div>
            <div><strong>Recording:</strong> {recording ? "yes" : "no"}</div>
            <div><strong>ScreenShare:</strong> {screenSharing ? "yes" : "no"}</div>
            <div><strong>Last ping:</strong> {new Date(lastPingRef.current).toLocaleTimeString()}</div>
          </div>

          <div className="diag-logs">
            <h5>Recent diag events</h5>
            <div className="diag-list">
              {diagMessages.slice().reverse().map((d, i) => <div key={i} className="diag-item">{d}</div>)}
            </div>
          </div>

          <div className="diag-actions">
            <button onClick={() => { setDiagMessages([]); pushDiag("[diag] cleared"); }}>Clear</button>
            <button onClick={() => {
              // quick dump to console for troubleshooting
              console.log("PeersRef:", peersRef.current);
              console.log("PeerVideoRefs:", peerVideoRefs.current);
              console.log("Participants:", participants);
              pushDiag("[diag] dumped to console");
              alert("Diagnostics dumped to console.");
            }}>Dump to Console</button>
          </div>
        </aside>
      )}
    </div>
  );
}

